import React from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { RMEResponse } from 'utils/api'

interface Well {
  id: number
  outputCells: number
  estimatedFrequency: number
}

interface EstimatesTableProps {
  metricsResult: RMEResponse | null
  wells: Well[]
  experimentGoal?: 'purity' | 'yield'
  isInternalUser?: boolean
}

const EstimatesTable: React.FC<EstimatesTableProps> = ({
  metricsResult,
  wells,
  experimentGoal,
  isInternalUser,
}) => {
  // Helper function to convert fraction to percentage - rounded to nearest whole percent
  const formatPercentage = (fraction: number): string => {
    return `${Math.round(fraction * 100)}%`
  }

  // Calculate overall purity and yield using weighted averages by output cells
  const calculateOverallMetrics = () => {
    if (
      !metricsResult?.well_metrics ||
      metricsResult.well_metrics.length === 0 ||
      wells.length === 0
    ) {
      return { purity: 'N/A', yield: 'N/A' }
    }

    let totalWeightedPurity = 0
    let totalWeightedYield = 0
    let totalCells = 0

    // Calculate weighted sums using output cells as weights
    metricsResult.well_metrics.forEach((wellMetric, index) => {
      if (index < wells.length) {
        const { outputCells } = wells[index]

        // Only include wells with output cells > 0 in the calculation
        if (outputCells > 0) {
          totalWeightedPurity += wellMetric.purity_fraction * outputCells
          totalWeightedYield += wellMetric.yield_fraction * outputCells
          totalCells += outputCells
        }
      }
    })

    if (totalCells === 0) {
      return { purity: 'N/A', yield: 'N/A' }
    }

    const weightedAvgPurity = totalWeightedPurity / totalCells
    const weightedAvgYield = totalWeightedYield / totalCells

    return {
      purity: formatPercentage(weightedAvgPurity),
      yield: formatPercentage(weightedAvgYield),
    }
  }

  const overallMetrics = calculateOverallMetrics()

  // Format well metrics for display
  const getWellMetrics = (_wellId: number, metricsIndex: number) => {
    if (!metricsResult?.well_metrics || !metricsResult.well_metrics[metricsIndex]) {
      return { purity: 'N/A', yield: 'N/A' }
    }

    const well = metricsResult.well_metrics[metricsIndex]
    return {
      purity: well.purity_fraction > 0 ? formatPercentage(well.purity_fraction) : 'N/A',
      yield: well.yield_fraction > 0 ? formatPercentage(well.yield_fraction) : 'N/A',
    }
  }

  // Decide which columns to show
  const showPurityColumn =
    Boolean(isInternalUser) || experimentGoal === 'purity' || experimentGoal === undefined
  const showYieldColumn =
    Boolean(isInternalUser) || experimentGoal === 'yield' || experimentGoal === undefined

  return (
    <Box sx={{ mt: 2 }}>
      <TableContainer
        sx={{
          border: `1px solid ${'#d0d0d0'}`,
          borderRadius: 1,
          backgroundColor: '#ffffff',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  color: '#111111',
                  borderBottom: `1px solid ${'#d0d0d0'}`,
                  borderRight: `1px solid ${'#d0d0d0'}`,
                  py: 0.75,
                }}
              >
                Estimates
              </TableCell>
              {showPurityColumn && (
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    color: '#111111',
                    borderBottom: `1px solid ${'#d0d0d0'}`,
                    borderRight: `1px solid ${'#d0d0d0'}`,
                    py: 0.75,
                  }}
                >
                  Purity
                </TableCell>
              )}
              {showYieldColumn && (
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    color: '#111111',
                    borderBottom: `1px solid ${'#d0d0d0'}`,
                    py: 0.75,
                  }}
                >
                  Yield
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#111111',
                  borderBottom: `1px solid ${'#d0d0d0'}`,
                  borderRight: `1px solid ${'#d0d0d0'}`,
                  py: 0.5,
                }}
              >
                Overall
              </TableCell>
              {showPurityColumn && (
                <TableCell
                  align="center"
                  sx={{
                    fontSize: '14px',
                    color: '#111111',
                    borderBottom: `1px solid ${'#d0d0d0'}`,
                    borderRight: `1px solid ${'#d0d0d0'}`,
                    py: 0.5,
                  }}
                >
                  {overallMetrics.purity}
                </TableCell>
              )}
              {showYieldColumn && (
                <TableCell
                  align="center"
                  sx={{
                    fontSize: '14px',
                    color: '#111111',
                    borderBottom: `1px solid ${'#d0d0d0'}`,
                    py: 0.5,
                  }}
                >
                  {overallMetrics.yield}
                </TableCell>
              )}
            </TableRow>

            {wells
              .filter((well) => well.outputCells > 0 || well.estimatedFrequency > 0)
              .map((well, displayIndex, filteredArray) => {
                // displayIndex is the position in the metrics array (0, 1, 2, 3...)
                const wellMetrics = getWellMetrics(well.id, displayIndex)
                const isLastRow = displayIndex === filteredArray.length - 1
                return (
                  <TableRow key={`well-${well.id}`}>
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#111111',
                        borderBottom: isLastRow ? 'none' : `1px solid ${'#d0d0d0'}`,
                        borderRight: `1px solid ${'#d0d0d0'}`,
                        py: 0.5,
                      }}
                    >
                      Well {well.id}
                    </TableCell>
                    {showPurityColumn && (
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: '14px',
                          color: '#111111',
                          borderBottom: isLastRow ? 'none' : `1px solid ${'#d0d0d0'}`,
                          borderRight: `1px solid ${'#d0d0d0'}`,
                          py: 0.5,
                        }}
                      >
                        {wellMetrics.purity}
                      </TableCell>
                    )}
                    {showYieldColumn && (
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: '14px',
                          color: '#111111',
                          borderBottom: isLastRow ? 'none' : `1px solid ${'#d0d0d0'}`,
                          py: 0.5,
                        }}
                      >
                        {wellMetrics.yield}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default EstimatesTable
